import { classes } from "polytype";
import type {
  ContractItemData,
  ContractItemTerminationCreateRequestData,
} from "./types.js";
import { DataModel, ReferenceModel } from "../../base/index.js";
import { type AsyncResourceVariant, provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import { ContractArticle } from "../ContractArticle/index.js";
import { Money } from "../../base/Money.js";
import { ContractItemReference } from "./ContractItemReference.js";
import { ContractTermination } from "../ContractTermination/index.js";
import { TariffChange } from "../TariffChange/index.js";
import type { TariffChangeRequestData } from "../../order/index.js";
import { Order } from "../../order/index.js";
import { DateTime } from "luxon";
import { Project } from "../../project/index.js";

export class ContractItem extends ReferenceModel {
  public readonly contractId: string;

  public constructor(contractId: string, id: string) {
    super(id);
    this.contractId = contractId;
  }

  public static ofId(contractId: string, id: string) {
    return new ContractItem(contractId, id);
  }

  public static find = provideReact(
    async (contractId: string, contractItemId: string) => {
      const data = await config.behaviors.contractItem.find(
        contractId,
        contractItemId,
      );
      if (data !== undefined) {
        return new ContractItemDetailed(contractId, data);
      }
    },
  );

  public static get = provideReact(
    async (contractId: string, contractItemId: string) => {
      const item = await this.find(contractId, contractItemId);
      assertObjectFound(item, "contractItem", contractItemId);
      return item;
    },
  );

  public getDetailed = provideReact(() => {
    return ContractItem.get(this.contractId, this.id);
  }, [this.id]) as AsyncResourceVariant<() => Promise<ContractItemDetailed>>;

  public findDetailed = provideReact(() => {
    return ContractItem.get(this.contractId, this.id);
  }, [this.id]) as AsyncResourceVariant<
    () => Promise<ContractItemDetailed | undefined>
  >;

  public async terminate(data: ContractItemTerminationCreateRequestData) {
    await config.behaviors.contractItem.terminate(
      this.contractId,
      this.id,
      data,
    );
  }

  public async cancelTermination() {
    await config.behaviors.contractItem.cancelTermination(
      this.contractId,
      this.id,
    );
  }

  public async createTariffChange(data: TariffChangeRequestData) {
    await Order.createTariffChange(data);
  }

  public async cancelTariffChange() {
    await config.behaviors.contractItem.cancelTariffChange(
      this.contractId,
      this.id,
    );
  }
}

export class ContractItemCommon extends classes(
  DataModel<ContractItemData>,
  ContractItem,
) {
  public readonly itemId: string;
  public readonly description: string;
  public readonly totalPrice: Money;
  public readonly totalYearlyPrice: Money;
  public readonly freeTrialDays?: number;
  public readonly orderDate?: DateTime;
  public readonly project?: Project;
  public readonly aggregateReference?: ContractItemReference;
  public readonly activationDate?: DateTime;
  public readonly nextPossibleDowngradeDate?: DateTime;
  public readonly nextPossibleTerminationDate?: DateTime;
  public readonly nextPossibleUpgradeDate?: DateTime;
  public readonly invoiceStop?: DateTime;
  public readonly termination?: ContractTermination;
  public readonly tariffChange?: TariffChange;
  public readonly contractPeriod?: number;
  public readonly isActivated: boolean;
  public readonly isBaseItem: boolean;
  public readonly invoicingPeriod?: number;
  public readonly freeTrialUntil?: DateTime;
  public readonly articleName: string;

  public constructor(contractId: string, data: ContractItemData) {
    super([data], [contractId, data.itemId]);
    this.description = data.description;
    this.itemId = data.itemId;
    this.isActivated = data.isActivated;
    this.project = data.groupByProjectId
      ? Project.ofId(data.groupByProjectId)
      : undefined;
    this.isBaseItem = data.isBaseItem;
    this.aggregateReference = data.aggregateReference
      ? new ContractItemReference(data.aggregateReference)
      : undefined;
    this.totalPrice = Money({
      amount: data.totalPrice.value,
      currency: "EUR",
    });
    this.totalYearlyPrice = Money({
      amount: this.totalPrice.getAmount() * 12,
      currency: "EUR",
    });
    this.freeTrialDays = data.freeTrialDays;
    if (data.activationDate) {
      this.activationDate = DateTime.fromISO(data.activationDate);
    }
    if (data.nextPossibleDowngradeDate) {
      this.nextPossibleDowngradeDate = DateTime.fromISO(
        data.nextPossibleDowngradeDate,
      );
    }
    if (data.nextPossibleTerminationDate) {
      this.nextPossibleTerminationDate = DateTime.fromISO(
        data.nextPossibleTerminationDate,
      );
    }
    if (data.nextPossibleUpgradeDate) {
      this.nextPossibleUpgradeDate = DateTime.fromISO(
        data.nextPossibleUpgradeDate,
      );
    }
    if (data.orderDate) {
      this.orderDate = DateTime.fromISO(data.orderDate);
    }
    if (data.invoiceStop) {
      this.invoiceStop = DateTime.fromISO(data.invoiceStop);
    }
    if (data.termination) {
      this.termination = new ContractTermination(data.termination);
    }
    if (data.tariffChange) {
      this.tariffChange = new TariffChange(data.tariffChange);
    }
    if (data.contractPeriod) {
      this.contractPeriod = data.contractPeriod;
    }
    if (data.invoicingPeriod) {
      this.invoicingPeriod = data.invoicingPeriod;
    }
    this.freeTrialUntil =
      data.isInFreeTrial &&
      this.activationDate &&
      this.activationDate.diff(DateTime.now()).toMillis() > 0
        ? this.activationDate
        : undefined;
    this.articleName = data.articles[0]?.name ?? "";
  }
}

export class ContractItemDetailed extends ContractItemCommon {
  public readonly articles: ContractArticle[];

  public constructor(contractId: string, data: ContractItemData) {
    super(contractId, data);

    this.articles = data.articles.map(
      (articleData) => new ContractArticle(articleData, this.id),
    );
  }
}
